import { createContext, useContext } from 'react';

export type Language = 'NL' | 'FR' | 'EN';

export const translations = {
  NL: {
    nav: {
      features: 'Aanbod',
      solutions: 'Oplossingen',
      signIn: 'Aanmelden'
    },
    partners: {
      title: 'Onze huidige partners:',
      subtitle: 'Deze toonaangevende bedrijven in de markt vertrouwen Jaimy en hun netwerk voor het beheer van hun onderhouden, herstellingen of installaties.'
    },
    hero: {
      title: 'Jaimy, jouw link naar techniekers.',
      subtitle: 'Verbind jouw klanten met gekwalificeerde professionals voor de installatie van jouw producten.\n\nVoeg onze onderhoudsservice en hersteldienst naadloos toe aan jouw serviceaanbod en verhoog zo jouw klantervaring en klanttevredenheid.',
      features: {
        installation: {
          title: 'Installatie',
          description: 'Professionele installatie van jouw producten.'
        },
        maintenance: {
          title: 'Onderhoud',
          description: 'Regelmatig onderhoud, inspecties of keuringen.'
        },
        repair: {
          title: 'Herstelling',
          description: 'Snelle en efficiënte herstellingen via 24/7 assistentie.'
        }
      },
      cta: {
        primary: 'Demo aanvragen',
        secondary: 'Demo aanvragen'
      }
    },
    features: {
      title: 'Waarom kies je als bedrijf voor Jaimy?',
      subtitle: 'Jaimy (by Belfius) biedt flexibele en schaalbare oplossingen op maat van jouw bedrijf. Van eenmalige installaties tot gepland onderhoud of dringende herstellingen.\n\nDankzij één centraal platform regel je alles eenvoudig, van planning tot facturatie, met minimale administratie en maximale efficiëntie. Bovendien voeg je moeiteloos extra diensten toe aan jouw klantenervaring, zonder extra operationele belasting voor jouw interne teams.',
      network: {
        title: 'Kwalitatief Netwerk',
        description: 'Jaimy heeft een netwerk van +1.500 gecertifiëerde en kwalitatieve vakmannen actief over het hele land, en dit in de belangrijkste sectoren om jouw bedrijf optimaal te kunnen ondersteunen.'
      },
      track: {
        title: 'Bewezen Track Record',
        description: 'Jaimy (by Belfius) verwerkt op jaarbasis 55.000 opdrachten, dit voor zowel huiseigenaren als B2B partners. De B2B tevredenheidsscore in 2024 was meer dan 97% bij meer dan 10.000 uitgevoerde interventies.'
      },
      integration: {
        title: 'Naadloze Integratie',
        description: 'Onze technologie ondersteunt jouw bedrijf om makkelijk installatie, herstel of onderhoudsdiensten toe te voegen als onderdeel van jullie klantenervaring. Verhoog jouw klanttevredenheid zonder zorgen.'
      }
    },
    solutions: {
      title: 'Wat is onze oplossing?',
      items: [
        {
          title: 'Kostenbesparing door outsourcing',
          description: 'Verminder vaste kosten door onderhoud, reparatie en installatie uit te besteden aan ons gecertificeerd netwerk van vakmannen.'
        },
        {
          title: 'Verlaagde operationele workload',
          description: 'Laat jouw interne teams focussen op kernactiviteiten terwijl wij de serviceplanning en uitvoering beheren.'
        },
        {
          title: 'Efficiënte workflows via slimme tools',
          description: 'Ons platform koppelt de juiste professional aan elke opdracht en optimaliseert automatisch de planning.'
        },
        {
          title: 'Kwaliteitsgarantie dankzij screening en monitoring',
          description: 'Al onze professionals zijn grondig gescreend en prestaties worden continu gemonitord voor consistentie.'
        },
        {
          title: 'Realtime updates en opvolging',
          description: 'Volg opdrachten live, ontvang meldingen en behoud overzicht met onze gebruiksvriendelijke interface.'
        },
        {
          title: 'Directe integratie in jouw (na)verkoopsproces',
          description: 'Integreer onze oplossing naadloos in jouw systemen voor eenvoudige administratieve opvolging en tevreden klanten.'
        }
      ]
    },
    dashboard: {
      title: 'Dienst Boeken',
      subtitle: 'Selecteer een dienst om in te plannen of vraag dringende bijstand',
      search: 'Zoek diensten...',
      viewCalendar: 'Bekijk Agenda',
      urgentHelp: {
        title: 'Dringende bijstand nodig?',
        description: 'Krijg prioritaire service voor noodsituaties',
        button: 'Vraag Spoeddienst Aan'
      },
      multipleServices: {
        title: 'Plan Meerdere Diensten',
        description: 'Boek verschillende diensten in één keer',
        button: 'Maak Gecombineerde Boeking'
      },
      services: {
        electrical: {
          title: 'Elektriciteitswerken',
          description: 'Installaties, herstellingen & keuringen'
        },
        heating: {
          title: 'Verwarmingsonderhoud',
          description: 'Service & onderhoud voor alle systemen'
        },
        plumbing: {
          title: 'Loodgieterij & Ontstopping',
          description: 'Snelle oplossing voor verstoppingen'
        },
        inspection: {
          title: 'Elektrische Keuring',
          description: 'Gecertificeerde veiligheidsinspecties'
        },
        safety: {
          title: 'Safety Check',
          description: 'Veiligheidscontroles en inspecties'
        },
        other: {
          title: 'Andere Diensten',
          description: 'Aangepaste onderhoudsaanvragen'
        }
      },
      urgentAvailable: 'Spoeddienst beschikbaar'
    },
    cta: {
      title: 'Klaar om jouw dienstverlening te transformeren?',
      subtitle: 'Sluit je aan bij toonaangevende bedrijven in de markt die Jaimy en hun netwerk reeds vertrouwen voor het beheer van hun onderhouden, herstellingen of installaties',
      button: 'Demo Aanvragen'
    },
    demo: {
      title: 'Demo Aanvragen',
      form: {
        name: 'Naam',
        company: 'Bedrijf',
        email: 'E-mailadres',
        phone: 'Telefoonnummer',
        message: 'Bericht (Optioneel)',
        submit: 'Demo Aanvragen',
        submitting: 'Verzenden...'
      },
      success: {
        title: 'Bedankt!',
        message: 'We nemen binnenkort contact met je op om een demo in te plannen.'
      }
    },
    footer: {
      phone: 'Bel ons',
      email: 'Stuur ons een e-mail',
      products: 'Onze producten',
      follow: 'Volg ons',
      terms: 'Algemene voorwaarden',
      privacy: 'Privacy & Cookies'
    },
    whitepaper: {
      title: 'BizComfort Whitepaper',
      subtitle: 'Leer over onze innovatieve aanpak voor technisch beheer',
      button: 'Download Whitepaper'
    },
    testimonials: {
      title: 'Wat onze klanten zeggen',
      subtitle: 'Ontdek hoe bedrijven hun operaties optimaliseren en kosten verlagen met BizComfort'
    },
    heroAnimated: {
      title: 'Slim uitbesteden van installatie, onderhoud & herstelling voor',
      rotatingTitles: [
        "Productfabrikanten", 
        "Retailketens", 
        "Vastgoedbeheerders", 
        "Facility managers"
      ],
      subtitle: 'Centraal beheer. Lokale uitvoering. Volledige controle.\nSlimmer plannen. Sneller schakelen.',
      demoButton: 'Demo aanvragen',
      whitepaperButton: 'Download Whitepaper'
    }
  },
  FR: {
    nav: {
      features: 'Fonctionnalités',
      solutions: 'Solutions',
      signIn: 'Connexion'
    },
    partners: {
      title: 'Nos partenaires actuels :',
      subtitle: 'Ces entreprises leaders du marché font confiance à Jaimy et à son réseau pour la gestion de leurs maintenances, réparations et installations.'
    },
    hero: {
      title: 'Jaimy, votre lien vers les techniciens',
      subtitle: 'Connectez vos clients avec des professionnels qualifiés pour l\'installation de vos produits.\n\nAjoutez nos services de maintenance et de réparation à votre offre de services et améliorez ainsi votre expérience client et la satisfaction client.',
      features: {
        installation: {
          title: 'Installation',
          description: 'Installation professionnelle de vos produits'
        },
        maintenance: {
          title: 'Maintenance',
          description: 'Entretien et inspection réguliers'
        },
        repair: {
          title: 'Réparation',
          description: 'Réparations rapides et efficaces'
        }
      },
      cta: {
        primary: 'Demander une démo',
        secondary: 'Demander une démo'
      }
    },
    features: {
      title: 'Pourquoi choisir Jaimy comme partenaire professionnel ?',
      subtitle: 'Jaimy (by Belfius) propose des solutions flexibles et évolutives sur mesure, des installations ponctuelles à l\'entretien planifié et aux réparations urgentes.\n\nGrâce à notre plateforme centrale, vous gérez tout facilement, de la planification à la facturation, avec une administration minimale et une efficacité maximale. De plus, vous ajoutez sans effort des services supplémentaires à votre expérience client, sans charge opérationnelle supplémentaire pour vos équipes internes.',
      network: {
        title: 'Réseau Qualifié',
        description: 'Jaimy dispose d\'un réseau de +1.500 professionnels certifiés et qualifiés actifs dans tout le pays, dans les secteurs les plus importants pour soutenir votre entreprise de manière optimale.'
      },
      track: {
        title: 'Bilan Prouvé',
        description: 'Jaimy (by Belfius) traite 55.000 missions par an, tant pour les propriétaires que pour les partenaires B2B. Le score de satisfaction B2B en 2024 était de plus de 97% pour plus de 10.000 interventions réalisées.'
      },
      integration: {
        title: 'Intégration Transparente',
        description: 'Notre technologie aide votre entreprise à ajouter facilement des services d\'installation, de réparation ou de maintenance dans le cadre de votre expérience client. Augmentez votre satisfaction client sans soucis.'
      }
    },
    solutions: {
      title: 'Notre Solution',
      items: [
        {
          title: 'Réduction des coûts par l\'externalisation',
          description: 'Réduisez les coûts fixes en sous-traitant la maintenance, la réparation et l\'installation à notre réseau certifié.'
        },
        {
          title: 'Charge de travail opérationnelle réduite',
          description: 'Permettez à vos équipes internes de se concentrer sur les activités principales pendant que nous gérons la planification et l\'exécution des services.'
        },
        {
          title: 'Flux de travail efficaces via des outils intelligents',
          description: 'Notre plateforme associe le bon professionnel à chaque mission et optimise automatiquement la planification.'
        },
        {
          title: 'Garantie de qualité grâce au screening et au monitoring',
          description: 'Tous nos professionnels sont rigoureusement sélectionnés et leurs performances sont constamment surveillées pour assurer la cohérence.'
        },
        {
          title: 'Mises à jour et suivi en temps réel',
          description: 'Suivez les missions en direct, recevez des notifications et gardez une vue d\'ensemble grâce à notre interface conviviale.'
        },
        {
          title: 'Intégration directe dans votre processus de (post-)vente',
          description: 'Intégrez notre solution de manière transparente dans vos systèmes pour un suivi administratif simple et des clients satisfaits.'
        }
      ]
    },
    dashboard: {
      title: 'Réserver un Service',
      subtitle: 'Sélectionnez un service à planifier ou demandez une assistance urgente',
      search: 'Rechercher des services...',
      viewCalendar: 'Voir le Calendrier',
      urgentHelp: {
        title: 'Besoin d\'aide urgente ?',
        description: 'Obtenez un service prioritaire pour les situations d\'urgence',
        button: 'Demander un Service d\'Urgence'
      },
      multipleServices: {
        title: 'Planifier Plusieurs Services',
        description: 'Réservez différents services en une fois',
        button: 'Créer une Réservation Combinée'
      },
      services: {
        electrical: {
          title: 'Travaux Électriques',
          description: 'Installations, réparations & inspections'
        },
        heating: {
          title: 'Maintenance Chauffage',
          description: 'Service & maintenance pour tous systèmes'
        },
        plumbing: {
          title: 'Plomberie & Débouchage',
          description: 'Résolution rapide des blocages'
        },
        inspection: {
          title: 'Inspection Électrique',
          description: 'Inspections de sécurité certifiées'
        },
        safety: {
          title: 'Contrôle de Sécurité',
          description: 'Contrôles et inspections de sécurité'
        },
        other: {
          title: 'Autres Services',
          description: 'Demandes de maintenance personnalisées'
        }
      },
      urgentAvailable: 'Service urgent disponible'
    },
    cta: {
      title: 'Prêt à transformer vos opérations de service ?',
      subtitle: 'Rejoignez les entreprises leaders qui font confiance à Jaimy pour gérer leur réseau de service',
      button: 'Demander une démo'
    },
    demo: {
      title: 'Demander une Démo',
      form: {
        name: 'Nom',
        company: 'Entreprise',
        email: 'Adresse e-mail',
        phone: 'Numéro de téléphone',
        message: 'Message (Optionnel)',
        submit: 'Demander une démo',
        submitting: 'Envoi en cours...'
      },
      success: {
        title: 'Merci !',
        message: 'Nous vous contacterons prochainement pour planifier votre démo.'
      }
    },
    footer: {
      phone: 'Appelez-nous',
      email: 'Envoyez-nous un e-mail',
      products: 'Nos produits',
      follow: 'Suivez-nous',
      terms: 'Conditions générales',
      privacy: 'Confidentialité & Cookies'
    },
    whitepaper: {
      title: 'BizComfort Whitepaper',
      subtitle: 'Découvrez notre approche innovante de la gestion technique',
      button: 'Télécharger le Whitepaper'
    },
    testimonials: {
      title: 'Ce que disent nos clients',
      subtitle: 'Découvrez comment les entreprises optimisent leurs opérations et réduisent les coûts avec BizComfort'
    },
    heroAnimated: {
      title: 'Externalisation intelligente d\'installation, maintenance & réparation pour',
      rotatingTitles: [
        "Fabricants de produits", 
        "Chaînes de magasins", 
        "Gestionnaires immobiliers", 
        "Facility managers"
      ],
      subtitle: 'Gestion centralisée. Exécution locale. Contrôle total.\nPlanification plus intelligente. Réaction plus rapide.',
      demoButton: 'Demander une démo',
      whitepaperButton: 'Télécharger le Whitepaper'
    }
  },
  EN: {
    nav: {
      features: 'Features',
      solutions: 'Solutions',
      signIn: 'Sign In'
    },
    partners: {
      title: 'Our current partners:',
      subtitle: 'These market-leading companies trust Jaimy and their network for managing their maintenance, repairs, and installations.'
    },
    hero: {
      title: 'Jaimy, your link to technicians',
      subtitle: 'Connect your customers with qualified professionals for the installation of your products.\n\nSeamlessly add our maintenance service and repair service to your service offering and increase your customer experience and satisfaction.',
      features: {
        installation: {
          title: 'Installation',
          description: 'Professional installation of your products'
        },
        maintenance: {
          title: 'Maintenance',
          description: 'Regular maintenance and inspection'
        },
        repair: {
          title: 'Repair',
          description: 'Fast and efficient repairs'
        }
      },
      cta: {
        primary: 'Request Demo',
        secondary: 'Request Demo'
      }
    },
    features: {
      title: 'Why choose Jaimy as your business partner?',
      subtitle: 'Jaimy (by Belfius) offers flexible and scalable solutions tailored to your needs, from one-time installations to planned maintenance and urgent repairs.\n\nThrough our central platform, you manage everything easily, from planning to invoicing, with minimal administration and maximum efficiency. Additionally, you effortlessly add extra services to your customer experience, without additional operational burden for your internal teams.',
      network: {
        title: 'Quality Network',
        description: 'Jaimy has a network of +1,500 certified and qualified professionals active across the country, in the most important sectors to optimally support your business.'
      },
      track: {
        title: 'Proven Track Record',
        description: 'Jaimy (by Belfius) processes 55,000 assignments annually, for both homeowners and B2B partners. The B2B satisfaction score in 2024 was over 97% with more than 10,000 interventions completed.'
      },
      integration: {
        title: 'Seamless Integration',
        description: 'Our technology supports your business to easily add installation, repair, or maintenance services as part of your customer experience. Increase your customer satisfaction worry-free.'
      }
    },
    solutions: {
      title: 'Our Solution',
      items: [
        {
          title: 'Cost Savings through Outsourcing',
          description: 'Reduce fixed costs by outsourcing maintenance, repair, and installation to our certified network.'
        },
        {
          title: 'Reduced Operational Workload',
          description: 'Let your internal teams focus on core activities while we manage service planning and execution.'
        },
        {
          title: 'Efficient Workflows via Smart Tools',
          description: 'Our platform matches the right professional to each task and automatically optimizes scheduling.'
        },
        {
          title: 'Quality Assurance through Screening and Monitoring',
          description: 'All our professionals are thoroughly screened and performance is continuously monitored for consistency.'
        },
        {
          title: 'Real-time Updates and Follow-up',
          description: 'Track assignments live, receive notifications, and maintain overview with our user-friendly interface.'
        },
        {
          title: 'Direct Integration in your (After-)Sales Process',
          description: 'Integrate our solution seamlessly into your systems for easy administrative follow-up and satisfied customers.'
        }
      ]
    },
    dashboard: {
      title: 'Book a Service',
      subtitle: 'Select a service to schedule or request urgent assistance',
      search: 'Search services...',
      viewCalendar: 'View Calendar',
      urgentHelp: {
        title: 'Need urgent assistance?',
        description: 'Get priority service for emergency situations',
        button: 'Request Emergency Service'
      },
      multipleServices: {
        title: 'Schedule Multiple Services',
        description: 'Book different services in one go',
        button: 'Create Combined Booking'
      },
      services: {
        electrical: {
          title: 'Electrical Work',
          description: 'Installations, repairs & inspections'
        },
        heating: {
          title: 'Heating Maintenance',
          description: 'Service & maintenance for all systems'
        },
        plumbing: {
          title: 'Plumbing & Declogging',
          description: 'Fast resolution for blockages'
        },
        inspection: {
          title: 'Electrical Inspection',
          description: 'Certified safety inspections'
        },
        safety: {
          title: 'Safety Check',
          description: 'Safety controls and inspections'
        },
        other: {
          title: 'Other Services',
          description: 'Custom maintenance requests'
        }
      },
      urgentAvailable: 'Urgent service available'
    },
    cta: {
      title: 'Ready to transform your service operations?',
      subtitle: 'Join leading companies who trust Jaimy to manage their service network',
      button: 'Request Demo'
    },
    demo: {
      title: 'Request a Demo',
      form: {
        name: 'Name',
        company: 'Company',
        email: 'Email',
        phone: 'Phone Number',
        message: 'Message (Optional)',
        submit: 'Request Demo',
        submitting: 'Submitting...'
      },
      success: {
        title: 'Thank you!',
        message: 'We\'ll be in touch shortly to schedule your demo.'
      }
    },
    footer: {
      phone: 'Call us',
      email: 'Send us an email',
      products: 'Our products',
      follow: 'Follow us',
      terms: 'Terms & Conditions',
      privacy: 'Privacy & Cookies'
    },
    whitepaper: {
      title: 'BizComfort Whitepaper',
      subtitle: 'Learn about our innovative approach to technical management',
      button: 'Download Whitepaper'
    },
    testimonials: {
      title: 'What our customers say',
      subtitle: 'Discover how companies optimize their operations and reduce costs with BizComfort'
    },
    heroAnimated: {
      title: 'Smart outsourcing of installation, maintenance & repair for',
      rotatingTitles: [
        "Product manufacturers", 
        "Retail chains", 
        "Property managers", 
        "Facility managers"
      ],
      subtitle: 'Central management. Local execution. Complete control.\nSmarter planning. Faster response.',
      demoButton: 'Request Demo',
      whitepaperButton: 'Download Whitepaper'
    }
  }
} as const;

export type TranslationKeys = typeof translations.EN;

type TranslationContextType = {
  lang: Language;
  t: TranslationKeys;
  setLang: (lang: Language) => void;
};

const TranslationContext = createContext<TranslationContextType>({
  lang: 'NL',
  t: translations.NL,
  setLang: () => {}
});

export const useTranslation = () => useContext(TranslationContext);

export const TranslationProvider = TranslationContext.Provider;